import {Component, Inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import moment from "moment";
import {UtilsService} from "../../shared/utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ApiRequestService} from "../../shared/api-request.service";
import {AppService} from "../../app.service";
import {NgForm, NgModel} from "@angular/forms";
import {AssetModel} from "../../models/asset.model";
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import {AssetsService} from "../assets.service";
import {SitesSelectorComponent} from "../../shared/sites-selector/sites-selector.component";
import {
  NetworkedAccountsSelectorComponent
} from "../../shared/networked-accounts-selector/networked-accounts-selector.component";
import {
  NetworkedUsersSelectorComponent
} from "../../shared/networked-users-selector/networked-users-selector.component";
import {TagsSelectorComponent} from "../../shared/tags-selector/tags-selector.component";
import {AssetsViewComponent} from "../assets-view/assets-view.component";
import {VendorsSelectorComponent} from "../../shared/vendors-selector/vendors-selector.component";
import {AssetTypesSelectorComponent} from "../../shared/asset-types-selector/asset-types-selector.component";
import {RBACService} from "../../shared/rbac.service";

@Component({
  selector: 'app-assets-edit',
  templateUrl: './assets-edit.component.html',
  styleUrls: ['./assets-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsEditComponent implements OnInit {

  inProgress: boolean = false;
  asset: AssetModel = new AssetModel();
  selectedFiles: File[] = [];
  selectedAssetTypeParentId: number | null = null;
  faHardHat = faHardHat;

  statusOptions = this.utils.toOptions(this.assetsService.STATUSES);
  conditionRatingOptions = this.utils.toOptions(this.assetsService.CONDITION_RATINGS);
  ownerTypeOptions = this.utils.toOptions(this.assetsService.OWNER_TYPES);
  costFrequencyOptions = this.utils.toOptions(this.assetsService.COST_FREQUENCIES);

  selectedLifecycleEndDate: moment.Moment;
  selectedExpectedReplacementDate: moment.Moment;
  selectedWarrantyExpiryDate: moment.Moment;
  selectedMaintenanceDueDate: moment.Moment;
  selectedInspectionDueDate: moment.Moment;
  selectedCostStartDate: moment.Moment;
  selectedCostEndDate: moment.Moment;
  lastInspectionAtDateTime: moment.Moment | null;

  // Today, used to cap date fields that record an event that has already happened
  // (purchase date and last inspection), so future dates cannot be selected.
  today: moment.Moment;

  costAmountInput: string | null = null;
  costAmountError: string = '';
  noiseRatingError: string = '';

  // RBAC permission flags: resolved once in ngOnInit(). Each is evaluated across both the account
  // level and the site level asset perspectives, because this dialog is opened from both and the
  // API accepts either (see AssetsController::assetPermissions()).
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Tags are optional metadata on an asset and belong to the Account: Tags feature, which is both a
  // separate RBAC feature and a separate plan feature. Unlike the required asset type, leaving them
  // out does not stop an asset being saved, so the button is hidden rather than the tags endpoint
  // being opened up to asset holders. Resolved once in ngOnInit().
  canViewTags: boolean = false;

  constructor(
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<AssetsEditComponent>,
    private api: ApiRequestService,
    public app: AppService,
    public assetsService: AssetsService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    const asset_id = this.dialogData?.asset_id;
    const site_id = this.dialogData?.site_id;

    // Resolve the RBAC permission flags first so they are always set, even when the access guard
    // below short circuits the rest of this method.
    this.canCreate = this.assetUserCanAccess(RBACService.RBAC_A.A_CREATE);
    this.canView = this.assetUserCanAccess(RBACService.RBAC_A.A_VIEW);
    this.canEdit = this.assetUserCanAccess(RBACService.RBAC_A.A_EDIT);
    this.canDelete = this.assetUserCanAccess(RBACService.RBAC_A.A_DELETE);
    this.canViewTags = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_TAGS, RBACService.RBAC_A.A_VIEW)
    ], true);

    const isEdit: boolean = typeof asset_id === 'number' && asset_id > 0;

    // Editing an existing asset needs the edit permission; creating a new one needs create.
    if ( (isEdit && !this.canEdit) || (!isEdit && !this.canCreate) ) {
      this.rbac.showGenericAccessDeniedMessage(
        isEdit
          ? 'You do not have permission to edit assets.'
          : 'You do not have permission to create assets.'
      );
      this.dialogRef.close();
      return;
    }
    this.today = moment().tz(this.app.account.timezone);

    if (typeof site_id === 'number' && site_id > 0) {
      this.asset.site_id = site_id;
    }

    if (isEdit) {
      this.find(asset_id);
    }
  }

  /**
   * Whether the user holds the given action on either asset perspective (the account level register
   * or a site's Asset Register). Mirrors the API, which accepts either.
   *
   * @param {string} action One of the RBACService.RBAC_A actions.
   * @return {boolean} True when the user holds the action in either perspective.
   */
  private assetUserCanAccess(action: string): boolean {
    return this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, action),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, action)
    ], false);
  }

  /**
   * Updates the selected expected replacement date when it changes.
   *
   * @param {any} value - The new selected expected replacement date.
   * @return {void}
   */
  onExpectedReplacementDateChange(value: any): void {
    this.selectedExpectedReplacementDate = value;
  }

  /**
   * Updates the selected warranty expiry date when it changes.
   *
   * @param {any} value - The new selected warranty expiry date.
   * @return {void}
   */
  onWarrantyExpiryDateChange(value: any): void {
    this.selectedWarrantyExpiryDate = value;
  }

  /**
   * Updates the selected maintenance due date when it changes.
   *
   * @param {any} value - The new selected maintenance due date.
   * @return {void}
   */
  onMaintenanceDueDateChange(value: any): void {
    this.selectedMaintenanceDueDate = value;
  }

  /**
   * Updates the selected inspection due date when it changes.
   *
   * @param {any} value - The new selected inspection due date.
   * @return {void}
   */
  onInspectionDueDateChange(value: any): void {
    this.selectedInspectionDueDate = value;
  }

  /**
   * Updates the selected lifecycle end date when it changes.
   *
   * @param {any} value - The new selected lifecycle end date.
   * @return {void}
   */
  onLifecycleEndDateChange(value: any): void {
    this.selectedLifecycleEndDate = value;
  }

  /**
   * Updates the selected cost start date when it changes.
   *
   * @param {any} value - The new selected lifecycle end date.
   * @return {void}
   */
  onCostStartDateChange(value: any): void {
    this.selectedCostStartDate = value;
  }

  /**
   * Updates the selected cost end date when it changes.
   *
   * @param {any} value - The new selected lifecycle end date.
   * @return {void}
   */
  onCostEndDateChange(value: any): void {
    this.selectedCostEndDate = value;
  }

  /**
   * Handles form submission by checking if the form is valid and whether the submit button has already been pressed.
   * If the form is invalid, a form validation error message is displayed.
   *
   * @param {NgForm} form - The NgForm object representing the form to be submitted.
   * @param {boolean} [autoCloseDialog=true] - Flag indicating whether to automatically close the dialog after submission.
   * @param {boolean} [continueWhenDone=false] - Indicates whether to continue after completion. Default is false.
   * @return {void}
   */
  onSubmit(form: NgForm, autoCloseDialog: boolean = true, continueWhenDone: boolean = false): void {
    // Check if the button was already pressed.
    if ( this.inProgress ) {
      return;
    }

    // Guard the action that will actually be performed: an existing asset is updated, a new one is
    // created, and those are separate permissions.
    if ( this.asset.id ) {
      if ( !this.canEdit ) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
        return;
      }
    } else {
      if ( !this.canCreate ) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to create assets.');
        return;
      }
    }

    // Check form validation.
    if ( !form.valid ) {
      this.utils.showFormValidationError('Please enter all required features.');
      return;
    }

    // Check Code Prefix validation.
    if(!this.assetsService.codePrefixValid(this.asset.code_prefix)){
      this.utils.showFormValidationError('Code Prefix must be 3-6 alphanumeric characters.');
      return;
    }

    this.inProgress = true;

    if(this.asset.owner_type != 'contractor_owned'){
      this.asset.contractor_account_id = null;
    }

    if(!this.assetsService.isNotOwned(this.asset.owner_type)){
      this.asset.owner_notes = null;
    }

    if(!this.assetsService.isLifecycleEndStatus(this.asset.status)){
      this.asset.lifecycle_end_at = null;
      this.asset.lifecycle_end_reason = null;
    }

    if(!this.assetsService.isRecurringPayment(this.asset.owner_type)){
      this.asset.cost_frequency = null;
      this.asset.cost_end_at = null;
    }

    // Check if an Expected Replacement date was selected before attempting to use formatting functions.
    if ( this.asset.expected_replacement_at && this.selectedExpectedReplacementDate ) {
      this.asset.expected_replacement_at = this.selectedExpectedReplacementDate.format('YYYY-MM-DD');
    }

    // Check if a Warranty Expiry date was selected before attempting to use formatting functions.
    if ( this.asset.warranty_expiry_date && this.selectedWarrantyExpiryDate ) {
      this.asset.warranty_expiry_date = this.selectedWarrantyExpiryDate.format('YYYY-MM-DD');
    }

    // Check if a Maintenance Due date was selected before attempting to use formatting functions.
    if ( this.asset.maintenance_due_date && this.selectedMaintenanceDueDate ) {
      this.asset.maintenance_due_date = this.selectedMaintenanceDueDate.format('YYYY-MM-DD');
    }

    // Check if an Inspection Due date was selected before attempting to use formatting functions.
    if ( this.asset.next_inspection_due_date && this.selectedInspectionDueDate ) {
      this.asset.next_inspection_due_date = this.selectedInspectionDueDate.format('YYYY-MM-DD');
    }

    // Check if a Lifecycle End date was selected before attempting to use formatting functions.
    if ( this.asset.lifecycle_end_at && this.selectedLifecycleEndDate ) {
      this.asset.lifecycle_end_at = this.selectedLifecycleEndDate.format('YYYY-MM-DD');
    }

    // Check if a Cost Start date was selected before attempting to use formatting functions.
    if ( this.asset.cost_start_at && this.selectedCostStartDate ) {
      this.asset.cost_start_at = this.selectedCostStartDate.format('YYYY-MM-DD');
    }

    // Check if a Cost End date was selected before attempting to use formatting functions.
    if ( this.asset.cost_end_at && this.selectedCostEndDate ) {
      this.asset.cost_end_at = this.selectedCostEndDate.format('YYYY-MM-DD');
    }

    // Convert the selected Last Inspection date and time to a unix timestamp.
    if ( this.lastInspectionAtDateTime && this.lastInspectionAtDateTime instanceof moment ) {
      const lastInspectionAtDateTime: moment.Moment = moment.tz(this.lastInspectionAtDateTime.format('M/D/YYYY, h:mm:ss a'), 'M/D/YYYY, h:mm:ss a', this.app.account.timezone);
      this.asset.last_inspection_at = lastInspectionAtDateTime.unix();
    } else {
      this.asset.last_inspection_at = null;
    }

    !this.asset.id ? this.create(autoCloseDialog) : this.update(autoCloseDialog, continueWhenDone);
  }

  /**
   * Finds a Asset by its ID.
   *
   * @param {number} id - The ID of the Vendor.
   * @return {void}
   */
  private find(id: number): void {
    // Make the API request.
    this.api.makeRequest('get', `v2/assets/${id}`)
      .then((response) => {
        this.asset = response.data as AssetModel;

        // Extract Contractor ids.
        this.asset.contractor_ids = this.asset.contractors ? this.asset.contractors.map((contractor) => contractor.id) : [];

        // Extract User ids.
        this.asset.user_ids = this.asset.users ? this.asset.users.map((user) => user.id) : [];

        // Extract Tag ids.
        this.asset.tag_ids = this.asset.tags ? this.asset.tags.map((tag) => tag.id) : [];

        // Convert the unix "last_inspection_at" timestamp to a moment date/time object.
        if ( this.asset.last_inspection_at ) {
          this.lastInspectionAtDateTime = moment.unix(this.asset.last_inspection_at);
        }

        this.costAmountInput = this.assetsService.normalizeCurrencyAmount(
          this.asset.cost_amount,
          this.app.account.currency_code
        );

        this.selectedAssetTypeParentId = this.asset.asset_type?.parent_id ?? null;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Displays a confirmation modal for deleting a Asset and archives it upon user confirmation.
   *
   * @return {void}
   */
  onDelete(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive assets.');
      return;
    }
    this.utils.showModal(
      `Remove Asset`,
      `Are you sure you want to archive Asset?`,
      () => {
        this.api.makeRequest('delete', `v2/assets/${this.asset.id}`)
          .then((response) => {
            this.utils.showToast(`"${this.asset.name}" has been archived.`);
            this.dialogRef.close(true);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Updates the Asset with new data.
   *
   * @param {boolean} [autoCloseDialog=true] - A boolean flag indicating whether to automatically close the dialog after update. Default is true.
   * @param {boolean} [continueWhenDone=false] Flag indicating whether to continue when the operation is done, defaults to false
   * @return void
   */
  private update(autoCloseDialog: boolean = true, continueWhenDone: boolean = false): void {
    this.api
      .makeRequest('put', `v2/assets/${this.asset.id}`, this.asset)
      .then((response) => {
        this.asset = response.data as AssetModel;
        this.onSuccess(`Updated: "${this.asset.name}"`, autoCloseDialog, continueWhenDone);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Creates a new Asset.
   *
   * @param {boolean} [autoCloseDialog=true] - A boolean flag indicating whether to automatically close the dialog after creation. Default is true.
   * @param {boolean} [continueWhenDone=false] Flag indicating whether to continue when the operation is done, defaults to false
   * @return {void}
   */
  private create(autoCloseDialog: boolean = true, continueWhenDone: boolean = false): void {
    this.api
      .makeRequest('post', 'v2/assets', this.asset)
      .then((response) => {
        this.asset = response.data as AssetModel;
        this.onSuccess(`New Asset created.`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        // Upload files.
        this.uploadSelectedFiles(this.asset.id, (): void => {
          this.inProgress = false;
        });
      });
  }

  /**
   * Handles the success scenario by showing a toast message with the given message and closing the dialog if autoCloseDialog is true.
   *
   * @param {string} message - The message to display in the toast.
   * @param {boolean} autoCloseDialog - Whether the dialog should be automatically closed. Default value is true.
   * @param continueWhenDone
   * @return {void}
   */
  private onSuccess(message: string, autoCloseDialog: boolean = true, continueWhenDone: boolean = false): void {
    this.utils.showToast(message);

    // Open in viewing mode.
    if ( continueWhenDone ) {
      this.onViewAsset(this.asset.id);
    }

    // Check if the dialog should be closed.
    if ( autoCloseDialog || continueWhenDone ) {
      this.dialogRef.close(this.asset);
    }
  }

  /**
   * View the asset.
   *
   * @param asset_id The id of the asset to view.
   * @return {void}
   */
  onViewAsset(asset_id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view assets.');
      return;
    }
    this.utils.showComponentDialog(AssetsViewComponent, {
      asset_id: asset_id
    }, {
      width: '90%'
    });
  }

  /**
   * Select the asset type the asset should be associated with.
   *
   * @return {void}
   */
  onSelectAssetType(): void {
    this.utils.showComponentDialog(
      AssetTypesSelectorComponent,
      {
        selected: [this.asset.asset_type_id],
        parent_id: this.selectedAssetTypeParentId,
        multiple: false,
        required: true,
        can_create: true
      },
      {
        width: '1500px'
      },
      (selected_asset_type: any) => {
        if ( typeof selected_asset_type != 'undefined' ) {
          this.asset.asset_type_id = selected_asset_type?.id;
          this.selectedAssetTypeParentId = selected_asset_type?.parent_id ?? null;

          if(!this.asset.id && !this.asset.code_prefix) {
            this.asset.code_prefix = this.assetsService.generateCodePrefixFromAssetType(selected_asset_type?.name);
          }
        }
      }
    );
  }

  /**
   * Select the site the asset should be associated with.
   *
   * @return {void}
   */
  onSelectSite(): void {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: [this.asset.site_id],
        multiple: false,
        // Allow confirming with nothing selected so the user can deselect the
        // current site and disassociate the asset from it (mirrors the vendor selector).
        allow_no_selection: true
      },
      {
        width: '1200px'
      },
      (selected_site_id: number) => {
        if ( typeof selected_site_id != 'undefined' ) {
          this.asset.site_id = selected_site_id > 0 ? selected_site_id : null;
        }
      }
    );
  }

  /**
   * Select contractor owner to associate with this asset.
   *
   * @return {void}
   */
  onSelectContractorOwner(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: [this.asset.contractor_account_id],
        multiple: false,
        can_clear_close: true
      },
      {
        width: '1200px'
      },
      (selected_contractor_account_id: number) => {
        if ( typeof selected_contractor_account_id != 'undefined' ) {
          this.asset.contractor_account_id = selected_contractor_account_id > 0 ? selected_contractor_account_id : null;
        }
      }
    );
  }

  /**
   * Select the vendor the asset should be associated with.
   *
   * @return {void}
   */
  onSelectVendor(): void {
    this.utils.showComponentDialog(
      VendorsSelectorComponent,
      {
        selected: [this.asset.vendor_id],
        multiple: false,
        required: false
      },
      {
        width: '1200px'
      },
      (selected_vendor_id: number) => {
        if ( typeof selected_vendor_id != 'undefined' ) {
          this.asset.vendor_id = selected_vendor_id > 0 ? selected_vendor_id : null;
        }
      }
    );
  }

  /**
   * Select contractors to associate with this asset.
   *
   * @return {void}
   */
  onSelectContractors(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: this.asset.contractor_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          // Set the newly selected ids.
          this.asset.contractor_ids = results;
        }
      }
    );
  }

  /**
   * Select users to associate with this asset.
   *
   * @return {void}
   */
  onSelectUsers(): void {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.asset.user_ids,
        multiple: true,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: any) => {
        if (typeof results !== 'undefined') {
          // Set the newly selected ids.
          this.asset.user_ids = results;
        }
      }
    );
  }

  /**
   * Select tags to associate with this asset.
   *
   * @return {void}
   */
  onSelectTags(): void {
    if (!this.canViewTags) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view tags.');
      return;
    }

    this.utils.showComponentDialog(
      TagsSelectorComponent,
      {
        selected: this.asset.tag_ids,
        multiple: true},
      {
        width: '1200px'
      },(results: number[]) => {
        if (!results) { return; }
        this.asset.tag_ids = results;
      }
    );
  }

  /**
   * Uploads the selected files to the server for the specified resource ID.
   * Provides a callback function to be executed after the upload process completes.
   *
   * @param {number} id - The unique identifier of the target resource where files will be uploaded.
   * @param {function} [callback] - Optional callback function executed after the upload process is complete.
   * @return {void} This method does not return a value.
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/asset/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    } else {
      if ( typeof callback === 'function' ) {
        callback();
      }
    }
  }

  /**
   * If it is a new asset, add the files to the selected files array.
   * Existing asset files will be handled by the file manager.
   *
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Retrieves the details of the output date and time based on the provided event.
   *
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputDateTimeDetails(event: any): void {
    this.lastInspectionAtDateTime = event;
  }

  /**
   * Validate and normalize cost amount on user input change.
   *
   * @param {string | number | null} value - The input value representing the cost amount.
   * @return {void}
   */
  onCostAmountChange(value: string | number | null): void {
    this.costAmountError = '';

    const stringValue = value === null || typeof value === 'undefined'
      ? null
      : String(value);

    if (!this.assetsService.isValidCurrencyAmount(stringValue, this.app.account.currency_code)) {
      const minorUnits = this.assetsService.getCurrencyMinorUnits(this.app.account.currency_code);

      this.costAmountError = `Cost amount must be a valid number with up to ${minorUnits} decimal place${minorUnits === 1 ? '' : 's'}.`;
      return;
    }

    this.asset.cost_amount = stringValue === '' || stringValue === null
      ? null
      : Number(stringValue);
  }

  /**
   * Normalize the display value up to 3 decimal places on blur.
   *
   * @return {void}
   */
  onCostAmountBlur(): void {
    const normalized = this.assetsService.normalizeCurrencyAmount(
      this.costAmountInput,
      this.app.account.currency_code
    );

    this.costAmountInput = normalized;

    // Convert to number for model/API
    this.asset.cost_amount = normalized !== null ? Number(normalized) : null;
  }

  /**
   * Validate and normalize noise rating on user input change.
   *
   * @param value
   * @return {void}
   */
  onNoiseRatingChange(value: number | string | null): void {
    this.noiseRatingError = '';

    if (value === null || value === '' || typeof value === 'undefined') {
      this.asset.noise_rating_db = null;
      return;
    }

    const parsedValue = Number(value);

    if (isNaN(parsedValue)) {
      this.asset.noise_rating_db = null;
      this.noiseRatingError = 'Noise rating must be a valid number.';
      return;
    }

    if (!Number.isInteger(parsedValue)) {
      this.asset.noise_rating_db = parsedValue;
      this.noiseRatingError = 'Noise rating must be a whole number.';
      return;
    }

    if (parsedValue < 0 || parsedValue > 140) {
      this.asset.noise_rating_db = parsedValue;
      this.noiseRatingError = 'Noise rating must be between 0 and 140 dB.';
      return;
    }

    this.asset.noise_rating_db = parsedValue;
  }

  /**
   * Normalize noise rating on blur.
   *
   * @return {void}
   */
  onNoiseRatingBlur(): void {
    if (this.asset.noise_rating_db === null || typeof this.asset.noise_rating_db === 'undefined') {
      return;
    }

    this.asset.noise_rating_db = Math.trunc(this.asset.noise_rating_db);
  }

  // Exposed so the template can plan gate the site selector, which belongs to the Sites feature.
  protected readonly AppService = AppService;

}
